import React, {useEffect, useRef, useState} from 'react'
import axios from 'axios'
import {
    Alert, Autocomplete, Box, Button, Checkbox, Chip, FormControlLabel, Grid, IconButton,
    Modal, Paper, TextField, Tooltip
} from '@mui/material'
import Typography from '@mui/material/Typography'
import Container from '@mui/material/Container'
import PostAddIcon from '@mui/icons-material/PostAdd'
import DeleteIcon from '@mui/icons-material/Delete'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import {DataGrid, GridColDef, GridToolbarContainer, GridToolbarQuickFilter} from '@mui/x-data-grid'

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 460,
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
}

function parseCsv(text: string): string[][] {
    return text
        .split(/\r?\n/)
        .slice(1)
        .filter(line => line.trim().length > 0)
        .map(line => line.split(',').map(cell => cell.trim()))
}

function PanelToolbar({onAdd, onUpload}: {onAdd: () => void; onUpload: (file: File) => void}) {
    const inputRef = useRef<HTMLInputElement>(null)
    return (
        <GridToolbarContainer>
            <GridToolbarQuickFilter/>
            <Tooltip title="Add">
                <Button onClick={onAdd}><PostAddIcon/></Button>
            </Tooltip>
            <Tooltip title="Import from CSV">
                <Button component="label">
                    <UploadFileIcon/>
                    <input ref={inputRef} type="file" accept=".csv" hidden
                        onChange={e => {
                            if (e.target.files?.[0]) { onUpload(e.target.files[0]); e.target.value = '' }
                        }}
                    />
                </Button>
            </Tooltip>
        </GridToolbarContainer>
    )
}

// ── Rituals ───────────────────────────────────────────────────────────────────

function RitualsPanel() {
    const [rituals, setRituals] = useState<any[]>([])
    const [itemTypes, setItemTypes] = useState<{id: number; name: string}[]>([])
    const [open, setOpen] = useState(false)
    const [nameError, setNameError] = useState<string | null>(null)
    const [form, setForm] = useState({name: '', aspect: '', extra_text: false, can_target_any: false, difficulty_cost_formula: '', reagent_cost_formula: '', max_per_item: ''})
    const [selectedTypes, setSelectedTypes] = useState<{id: number; name: string}[]>([])
    const [uploadResult, setUploadResult] = useState<{message: string; error: boolean} | null>(null)

    useEffect(() => {
        axios.get('/admin/magic-items/rituals').then(({data}) => setRituals(data))
        axios.get('/admin/magic-items/types').then(({data}) => setItemTypes(data))
    }, [])

    function handleClose() {
        setOpen(false)
        setNameError(null)
        setForm({name: '', aspect: '', extra_text: false, can_target_any: false, difficulty_cost_formula: '', reagent_cost_formula: '', max_per_item: ''})
        setSelectedTypes([])
    }

    async function create() {
        try {
            const {data} = await axios.post('/admin/magic-items/rituals', {
                ...form,
                difficulty_cost_formula: form.difficulty_cost_formula || null,
                reagent_cost_formula: form.reagent_cost_formula || null,
                max_per_item: form.max_per_item !== '' ? parseInt(form.max_per_item) : null,
                itemTypeIds: selectedTypes.map(t => t.id)
            })
            setRituals(prev => [...prev, data])
            handleClose()
        } catch (err: any) {
            if (err?.response?.status === 409) setNameError(err.response.data?.error ?? 'Name already exists.')
        }
    }

    async function uploadCsv(file: File) {
        setUploadResult(null)
        const text = await file.text()
        const rows = parseCsv(text)
        // CSV format: name,aspect,eligible_types (pipe-separated names),extra_text,can_target_any,difficulty_cost_formula,reagent_cost_formula,max_per_item
        const items = rows
            .filter(r => r.length >= 2 && r[0])
            .map(r => ({
                name: r[0],
                aspect: r[1] ?? '',
                eligible_type_names: r[2] ? r[2].split('|').map(s => s.trim()).filter(Boolean) : [],
                extra_text: (r[3] ?? '').toLowerCase() === 'true',
                can_target_any: (r[4] ?? '').toLowerCase() === 'true',
                difficulty_cost_formula: r[5] || undefined,
                reagent_cost_formula: r[6] || undefined,
                max_per_item: r[7] ? parseInt(r[7]) || undefined : undefined,
            }))
        if (items.length === 0) {
            setUploadResult({message: 'No valid rows found in CSV.', error: true})
            return
        }
        try {
            const {data} = await axios.post('/admin/magic-items/rituals/batch', {rituals: items})
            const fresh = await axios.get('/admin/magic-items/rituals')
            setRituals(fresh.data)
            setUploadResult({message: `Imported ${data.imported} ritual${data.imported === 1 ? '' : 's'} (duplicates skipped).`, error: false})
        } catch {
            setUploadResult({message: 'Upload failed. CSV format: name,aspect,Type1|Type2,extra_text', error: true})
        }
    }

    async function remove(id: number) {
        await axios.delete(`/admin/magic-items/rituals/${id}`)
        setRituals(prev => prev.filter(r => r.id !== id))
    }

    const columns: GridColDef[] = [
        {field: 'name', headerName: 'Name', flex: 1, headerAlign: 'center'},
        {field: 'aspect', headerName: 'Aspect', width: 140, headerAlign: 'center'},
        {
            field: 'eligible_items', headerName: 'Eligible Item Types', flex: 2, headerAlign: 'center',
            valueGetter: (params: any) =>
                (params.row.eligible_items ?? []).map((e: any) => e.itemType?.name).filter(Boolean).join(', ') || '—',
        },
        {field: 'extra_text', headerName: 'Extra Text', width: 100, headerAlign: 'center', align: 'center',
            renderCell: (params) => params.value ? '✓' : ''},
        {field: 'can_target_any', headerName: 'Any Item', width: 90, headerAlign: 'center', align: 'center',
            renderCell: (params) => params.value ? '✓' : ''},
        {field: 'difficulty_cost_formula', headerName: 'Difficulty Formula', flex: 1, headerAlign: 'center',
            valueGetter: (params: any) => params.row.difficulty_cost_formula ?? '—'},
        {field: 'reagent_cost_formula', headerName: 'Reagent Formula', flex: 1, headerAlign: 'center',
            valueGetter: (params: any) => params.row.reagent_cost_formula ?? '—'},
        {field: 'max_per_item', headerName: 'Max / Item', width: 100, headerAlign: 'center', align: 'center',
            valueGetter: (params: any) => params.row.max_per_item ?? '—'},
        {field: 'actions', headerName: '', width: 60, sortable: false,
            renderCell: (params) => (
                <Tooltip title="Delete"><span>
                    <IconButton size="small" color="error" onClick={() => remove(params.row.id)}>
                        <DeleteIcon fontSize="small"/>
                    </IconButton>
                </span></Tooltip>
            )
        }
    ]

    return <>
        <Paper elevation={2} sx={{p: 1}}>
            <Typography variant="h6" align="center">Rituals</Typography>
            {uploadResult && (
                <Alert severity={uploadResult.error ? 'error' : 'success'} onClose={() => setUploadResult(null)} sx={{mb: 1}}>
                    {uploadResult.message}
                </Alert>
            )}
            <DataGrid autoHeight density="compact" rows={rituals} columns={columns}
                getRowId={r => r.id}
                slots={{toolbar: () => <PanelToolbar onAdd={() => setOpen(true)} onUpload={uploadCsv}/>}}
                initialState={{pagination: {paginationModel: {pageSize: 15}}}}
            />
        </Paper>
        <Modal open={open} onClose={handleClose}>
            <Box sx={modalStyle}>
                <Typography variant="h6" gutterBottom>Add Ritual</Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField fullWidth label="Name" error={!!nameError} helperText={nameError ?? ''}
                            value={form.name}
                            onChange={e => {setForm({...form, name: e.target.value}); if (nameError) setNameError(null)}}/>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField fullWidth label="Aspect" value={form.aspect}
                            onChange={e => setForm({...form, aspect: e.target.value})}/>
                    </Grid>
                    <Grid item xs={12}>
                        <Autocomplete
                            multiple
                            options={itemTypes}
                            getOptionLabel={t => t.name}
                            value={selectedTypes}
                            onChange={(_e, v) => setSelectedTypes(v)}
                            isOptionEqualToValue={(o, v) => o.id === v.id}
                            renderTags={(value, getTagProps) =>
                                value.map((option, index) => (
                                    <Chip label={option.name} size="small" {...getTagProps({index})} key={option.id}/>
                                ))
                            }
                            renderInput={params => <TextField {...params} label="Eligible Item Types"/>}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <FormControlLabel
                            control={<Checkbox checked={form.extra_text}
                                onChange={e => setForm({...form, extra_text: e.target.checked})}/>}
                            label="Extra Text"
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <FormControlLabel
                            control={<Checkbox checked={form.can_target_any}
                                onChange={e => setForm({...form, can_target_any: e.target.checked})}/>}
                            label="Can Target Any Item"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField fullWidth label="Difficulty Cost Formula" placeholder="e.g. 2*x"
                            value={form.difficulty_cost_formula}
                            onChange={e => setForm({...form, difficulty_cost_formula: e.target.value})}/>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField fullWidth label="Reagent Cost Formula" placeholder="e.g. x+1"
                            value={form.reagent_cost_formula}
                            onChange={e => setForm({...form, reagent_cost_formula: e.target.value})}/>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Max Per Item" type="number" inputProps={{min: 1}}
                            value={form.max_per_item}
                            onChange={e => setForm({...form, max_per_item: e.target.value})}/>
                    </Grid>
                </Grid>
                <Container sx={{textAlign: 'center', mt: 2}}>
                    <Button variant="contained" disabled={!form.name || !form.aspect || !!nameError} onClick={create}>Add</Button>
                </Container>
            </Box>
        </Modal>
    </>
}

// ── Flaws ─────────────────────────────────────────────────────────────────────

function FlawsPanel() {
    const [flaws, setFlaws] = useState<any[]>([])
    const [open, setOpen] = useState(false)
    const [nameError, setNameError] = useState<string | null>(null)
    const [form, setForm] = useState({name: '', text: ''})
    const [uploadResult, setUploadResult] = useState<{message: string; error: boolean} | null>(null)

    useEffect(() => {
        axios.get('/admin/magic-items/flaws').then(({data}) => setFlaws(data))
    }, [])

    function handleClose() {
        setOpen(false)
        setNameError(null)
        setForm({name: '', text: ''})
    }

    async function create() {
        try {
            const {data} = await axios.post('/admin/magic-items/flaws', form)
            setFlaws(prev => [...prev, data])
            handleClose()
        } catch (err: any) {
            if (err?.response?.status === 409) setNameError(err.response.data?.error ?? 'Name already exists.')
        }
    }

    async function uploadCsv(file: File) {
        setUploadResult(null)
        const text = await file.text()
        const rows = parseCsv(text)
        const items = rows
            .filter(r => r.length >= 2 && r[0])
            .map(r => ({name: r[0], text: r[1] ?? ''}))
        if (items.length === 0) {
            setUploadResult({message: 'No valid rows found in CSV.', error: true})
            return
        }
        try {
            const {data} = await axios.post('/admin/magic-items/flaws/batch', {flaws: items})
            const fresh = await axios.get('/admin/magic-items/flaws')
            setFlaws(fresh.data)
            setUploadResult({message: `Imported ${data.imported} flaw${data.imported === 1 ? '' : 's'} (duplicates skipped).`, error: false})
        } catch {
            setUploadResult({message: 'Upload failed. CSV format: name,text', error: true})
        }
    }

    async function remove(id: number) {
        await axios.delete(`/admin/magic-items/flaws/${id}`)
        setFlaws(prev => prev.filter(f => f.id !== id))
    }

    const columns: GridColDef[] = [
        {field: 'name', headerName: 'Name', width: 200, headerAlign: 'center'},
        {field: 'text', headerName: 'Text', flex: 1, headerAlign: 'center'},
        {field: 'actions', headerName: '', width: 60, sortable: false,
            renderCell: (params) => (
                <Tooltip title="Delete"><span>
                    <IconButton size="small" color="error" onClick={() => remove(params.row.id)}>
                        <DeleteIcon fontSize="small"/>
                    </IconButton>
                </span></Tooltip>
            )
        }
    ]

    return <>
        <Paper elevation={2} sx={{p: 1}}>
            <Typography variant="h6" align="center">Flaws</Typography>
            {uploadResult && (
                <Alert severity={uploadResult.error ? 'error' : 'success'} onClose={() => setUploadResult(null)} sx={{mb: 1}}>
                    {uploadResult.message}
                </Alert>
            )}
            <DataGrid autoHeight density="compact" rows={flaws} columns={columns}
                getRowId={r => r.id}
                slots={{toolbar: () => <PanelToolbar onAdd={() => setOpen(true)} onUpload={uploadCsv}/>}}
                initialState={{pagination: {paginationModel: {pageSize: 15}}}}
            />
        </Paper>
        <Modal open={open} onClose={handleClose}>
            <Box sx={modalStyle}>
                <Typography variant="h6" gutterBottom>Add Flaw</Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField fullWidth label="Name" error={!!nameError} helperText={nameError ?? ''}
                            value={form.name}
                            onChange={e => {setForm({...form, name: e.target.value}); if (nameError) setNameError(null)}}/>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField fullWidth label="Text" multiline minRows={2} value={form.text}
                            onChange={e => setForm({...form, text: e.target.value})}/>
                    </Grid>
                </Grid>
                <Container sx={{textAlign: 'center', mt: 2}}>
                    <Button variant="contained" disabled={!form.name || !form.text || !!nameError} onClick={create}>Add</Button>
                </Container>
            </Box>
        </Modal>
    </>
}

// ── Item Types ────────────────────────────────────────────────────────────────

function ItemTypesPanel() {
    const [types, setTypes] = useState<any[]>([])
    const [open, setOpen] = useState(false)
    const [nameError, setNameError] = useState<string | null>(null)
    const [form, setForm] = useState({name: '', is_item: false})
    const [uploadResult, setUploadResult] = useState<{message: string; error: boolean} | null>(null)

    useEffect(() => {
        axios.get('/admin/magic-items/types').then(({data}) => setTypes(data))
    }, [])

    function handleClose() {
        setOpen(false)
        setNameError(null)
        setForm({name: '', is_item: false})
    }

    async function create() {
        try {
            const {data} = await axios.post('/admin/magic-items/types', form)
            setTypes(prev => [...prev, data])
            handleClose()
        } catch (err: any) {
            if (err?.response?.status === 409) setNameError(err.response.data?.error ?? 'Name already exists.')
        }
    }

    async function uploadCsv(file: File) {
        setUploadResult(null)
        const text = await file.text()
        const rows = parseCsv(text)
        // CSV format: name,is_item
        const items = rows
            .filter(r => r.length >= 1 && r[0])
            .map(r => ({name: r[0], is_item: (r[1] ?? '').toLowerCase() === 'true'}))
        if (items.length === 0) {
            setUploadResult({message: 'No valid rows found in CSV.', error: true})
            return
        }
        try {
            const {data} = await axios.post('/admin/magic-items/types/batch', {types: items})
            const fresh = await axios.get('/admin/magic-items/types')
            setTypes(fresh.data)
            setUploadResult({message: `Imported ${data.imported} item type${data.imported === 1 ? '' : 's'} (duplicates skipped).`, error: false})
        } catch {
            setUploadResult({message: 'Upload failed. CSV format: name', error: true})
        }
    }

    async function remove(id: number) {
        await axios.delete(`/admin/magic-items/types/${id}`)
        setTypes(prev => prev.filter(t => t.id !== id))
    }

    const columns: GridColDef[] = [
        {field: 'name', headerName: 'Name', flex: 1, headerAlign: 'center'},
        {field: 'is_item', headerName: 'Is Item', width: 90, headerAlign: 'center', align: 'center',
            renderCell: (params) => params.value ? '✓' : ''},
        {field: 'actions', headerName: '', width: 60, sortable: false,
            renderCell: (params) => (
                <Tooltip title="Delete"><span>
                    <IconButton size="small" color="error" onClick={() => remove(params.row.id)}>
                        <DeleteIcon fontSize="small"/>
                    </IconButton>
                </span></Tooltip>
            )
        }
    ]

    return <>
        <Paper elevation={2} sx={{p: 1}}>
            <Typography variant="h6" align="center">Item Types</Typography>
            {uploadResult && (
                <Alert severity={uploadResult.error ? 'error' : 'success'} onClose={() => setUploadResult(null)} sx={{mb: 1}}>
                    {uploadResult.message}
                </Alert>
            )}
            <DataGrid autoHeight density="compact" rows={types} columns={columns}
                getRowId={r => r.id}
                slots={{toolbar: () => <PanelToolbar onAdd={() => setOpen(true)} onUpload={uploadCsv}/>}}
                initialState={{pagination: {paginationModel: {pageSize: 15}}}}
            />
        </Paper>
        <Modal open={open} onClose={handleClose}>
            <Box sx={modalStyle}>
                <Typography variant="h6" gutterBottom>Add Item Type</Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField fullWidth label="Name" error={!!nameError} helperText={nameError ?? ''}
                            value={form.name}
                            onChange={e => {setForm({...form, name: e.target.value}); if (nameError) setNameError(null)}}/>
                    </Grid>
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={<Checkbox checked={form.is_item}
                                onChange={e => setForm({...form, is_item: e.target.checked})}/>}
                            label="Is Item"
                        />
                    </Grid>
                </Grid>
                <Container sx={{textAlign: 'center', mt: 2}}>
                    <Button variant="contained" disabled={!form.name || !!nameError} onClick={create}>Add</Button>
                </Container>
            </Box>
        </Modal>
    </>
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function MagicItemAdminPanel() {
    return <React.Fragment>
        <Grid container spacing={2} p={1}>
            <Grid item xs={12}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Typography variant="h4" align="center">Magic Item Management</Typography>
                </Paper>
            </Grid>
            <Grid item xs={12}>
                <RitualsPanel/>
            </Grid>
            <Grid item xs={12}>
                <FlawsPanel/>
            </Grid>
            <Grid item xs={12}>
                <ItemTypesPanel/>
            </Grid>
        </Grid>
    </React.Fragment>
}
